import React, { useState, useCallback } from "react";
import axios from "axios";
import OrderDuplicates from "./OrderDuplicates";

export default function OrderSync() {
  const API_URL = process.env.REACT_APP_API_URL;

  // View Mode State
  const [mainView, setMainView] = useState("single"); // "single" | "bulk"
  const [activeOperation, setActiveOperation] = useState("check"); // "check" | "push" | "pull"

  // Single Order State
  const [bookingId, setBookingId] = useState("");
  const [singleResult, setSingleResult] = useState(null);

  // Bulk Order State
  const [bulkBookingIds, setBulkBookingIds] = useState("");
  const [bulkResults, setBulkResults] = useState(null);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Auto-Sync Options
  const [syncPriority, setSyncPriority] = useState("shypv"); // "local" | "shypv"

  // Delete Options
  const [deleteFrom, setDeleteFrom] = useState("LOCAL"); // "LOCAL" | "SHYPV" | "BOTH"
  const [deleteResult, setDeleteResult] = useState(null);

  // Validation State  
  const [validationResult, setValidationResult] = useState(null);

  // AWB handed to the Duplicates panel when a check finds more than one local copy
  const [duplicateAwb, setDuplicateAwb] = useState("");

  // Notification State
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  // Styles (following NewNormalTaskCreation pattern)
  const styles = {
    container: {
      padding: "24px",
      backgroundColor: "#f5f7fa",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    title: {
      fontSize: "22px",
      fontWeight: "600",
      color: "#2d3748",
      margin: 0,
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      marginBottom: "20px",
      border: "1px solid #e8ecf1",
    },
    cardHeader: {
      padding: "16px 20px",
      borderBottom: "1px solid #e8ecf1",
      backgroundColor: "#fafbfc",
      borderRadius: "12px 12px 0 0",
    },
    cardTitle: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#374151",
      margin: 0,
    },
    cardBody: {
      padding: "20px",
    },
    formRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "20px",
    },
    formGroup: {
      marginBottom: "0",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: "500",
      color: "#4b5563",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      outline: "none",
      boxSizing: "border-box",
    },
    inputFocus: {
      borderColor: "#4f7df3",
      boxShadow: "0 0 0 3px rgba(79, 125, 243, 0.1)",
    },
    textarea: {
      width: "100%",
      padding: "12px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      minHeight: "120px",
      resize: "vertical",
      fontFamily: "inherit",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      cursor: "pointer",
      outline: "none",
    },
    btnPrimary: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#4f7df3",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnSecondary: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#4b5563",
      backgroundColor: "#f3f4f6",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnSuccess: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#10b981",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnWarning: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#f59e0b",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnDanger: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#ef4444",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnSmall: {
      padding: "6px 14px",
      fontSize: "13px",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)",
      gap: "16px",
      marginBottom: "20px",
    },
    statCard: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "20px",
      textAlign: "center",
      border: "1px solid #e8ecf1",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      transition: "transform 0.2s, box-shadow 0.2s",
    },
    statCardHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
    statValue: {
      fontSize: "28px",
      fontWeight: "700",
      marginBottom: "4px",
    },
    statLabel: {
      fontSize: "13px",
      color: "#6b7280",
    },
    toggleContainer: {
      display: "inline-flex",
      backgroundColor: "#f1f5f9",
      borderRadius: "14px",
      padding: "5px",
      marginBottom: "24px",
      marginLeft: "0",
      border: "1px solid #e2e8f0",
    },
    toggleBtn: {
      padding: "12px 28px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    toggleBtnActive: {
      backgroundColor: "#fff",
      color: "#1e40af",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    },
    toggleBtnInactive: {
      backgroundColor: "transparent",
      color: "#64748b",
    },
    tabs: {
      display: "flex",
      gap: "4px",
      padding: "4px",
      backgroundColor: "#f3f4f6",
      borderRadius: "10px",
      marginBottom: "20px",
    },
    tab: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    tabActive: {
      backgroundColor: "#fff",
      color: "#4f7df3",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    tabInactive: {
      backgroundColor: "transparent",
      color: "#6b7280",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "13px",
    },
    th: {
      padding: "12px 14px",
      textAlign: "left",
      fontWeight: "600",
      color: "#4b5563",
      backgroundColor: "#f9fafb",
      borderBottom: "1px solid #e5e7eb",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "12px 14px",
      borderBottom: "1px solid #e5e7eb",
      color: "#374151",
    },
    alert: {
      padding: "14px 18px",
      borderRadius: "10px",
      marginBottom: "20px",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    alertError: {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      border: "1px solid #fecaca",
    },
    alertSuccess: {
      backgroundColor: "#f0fdf4",
      color: "#16a34a",
      border: "1px solid #bbf7d0",
    },
    alertWarning: {
      backgroundColor: "#fffbeb",
      color: "#d97706",
      border: "1px solid #fde68a",
    },
    alertInfo: {
      backgroundColor: "#eff6ff",
      color: "#2563eb",
      border: "1px solid #bfdbfe",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      fontSize: "12px",
      fontWeight: "500",
      borderRadius: "12px",
      gap: "4px",
    },
    badgeSynced: { backgroundColor: "#d1fae5", color: "#065f46" },
    badgeLocalOnly: { backgroundColor: "#fef3c7", color: "#92400e" },
    badgeShypvOnly: { backgroundColor: "#dbeafe", color: "#1d4ed8" },
    badgeMismatch: { backgroundColor: "#fed7aa", color: "#c2410c" },
    badgeNotFound: { backgroundColor: "#fee2e2", color: "#dc2626" },
    badgeSuccess: { backgroundColor: "#d1fae5", color: "#065f46" },
    badgeFailed: { backgroundColor: "#fee2e2", color: "#dc2626" },
    resultCard: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      border: "1px solid #e8ecf1",
      overflow: "hidden",
      marginBottom: "16px",
    },
    resultHeader: {
      padding: "16px 20px",
      backgroundColor: "#f9fafb",
      borderBottom: "1px solid #e8ecf1",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    resultBody: {
      padding: "20px",
    },
    dataGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "20px",
    },
    dataBox: {
      backgroundColor: "#f9fafb",
      borderRadius: "10px",
      padding: "16px",
      border: "1px solid #e8ecf1",
    },
    dataBoxTitle: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    dataItem: {
      display: "flex",
      justifyContent: "space-between",
      padding: "8px 0",
      borderBottom: "1px solid #e8ecf1",
      fontSize: "13px",
    },
    dataLabel: {
      color: "#6b7280",
    },
    dataValue: {
      color: "#1f2937",
      fontWeight: "500",
    },
    notification: {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      padding: "24px 40px",
      borderRadius: "16px",
      fontSize: "16px",
      fontWeight: "500",
      zIndex: 10000,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      animation: "fadeIn 0.3s ease-out",
    },
    notificationSuccess: {
      backgroundColor: "#10b981",
      color: "#fff",
    },
    notificationError: {
      backgroundColor: "#ef4444",
      color: "#fff",
    },
    notificationWarning: {
      backgroundColor: "#f59e0b",
      color: "#fff",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#6b7280",
    },
    emptyIcon: {
      fontSize: "48px",
      marginBottom: "16px",
      opacity: 0.5,
    },
    spinner: {
      display: "inline-block",
      width: "20px",
      height: "20px",
      border: "2px solid #fff",
      borderRadius: "50%",
      borderTopColor: "transparent",
      animation: "spin 0.8s linear infinite",
    },
    actionButtons: {
      display: "flex",
      gap: "10px",
      marginTop: "16px",
    },
  };

  // API Functions
  const getBaseUrl = () => {
    return `${API_URL}shypvdriverapp/api/sync`;
  };

  // Show notification
  const displayNotification = (message, type = "success") => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  // CHECK - Single Order
  const checkSingleOrder = async () => {
    if (!bookingId.trim()) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");
    setSingleResult(null);

    try {
      const response = await axios.get(`${getBaseUrl()}/check/${bookingId.trim()}`);
      setSingleResult(response.data.data);
      displayNotification("Check completed successfully", "success");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to check order");
      displayNotification("Check failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // CHECK - Bulk Orders
  const checkBulkOrders = async () => {
    const ids = bulkBookingIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    if (ids.length === 0) {
      displayNotification("Please enter at least one Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");
    setBulkResults(null);

    try {
      const response = await axios.post(`${getBaseUrl()}/check/bulk`, ids);
      setBulkResults(response.data.data);
      displayNotification(`Checked ${ids.length} orders`, "success");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to check orders");
      displayNotification("Bulk check failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // PUSH - Single Order
  const pushSingleOrder = async (bkId = null) => {
    const idToPush = bkId || bookingId.trim();
    if (!idToPush) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${getBaseUrl()}/push/${idToPush}`);
      setSingleResult(prev => ({
        ...prev,
        pushResult: response.data.data
      }));
      displayNotification(response.data.data.message || "Push completed", 
        response.data.data.success ? "success" : "warning");
      // Refresh check after push
      if (bkId) {
        setTimeout(() => checkSingleOrder(), 1000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to push order");
      displayNotification("Push failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // PUSH - Bulk Orders
  const pushBulkOrders = async (selectedIds = null) => {
    const ids = selectedIds || bulkBookingIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    if (ids.length === 0) {
      displayNotification("Please select orders to push", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${getBaseUrl()}/push/bulk`, ids);
      setBulkResults(prev => ({
        ...prev,
        pushSummary: response.data.data
      }));
      const data = response.data.data;
      displayNotification(`Pushed: ${data.success} success, ${data.failed} failed`, 
        data.failed > 0 ? "warning" : "success");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to push orders");
      displayNotification("Bulk push failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // PULL - Single Order
  const pullSingleOrder = async (bkId = null) => {
    const idToPull = bkId || bookingId.trim();
    if (!idToPull) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${getBaseUrl()}/pull/${idToPull}`);
      setSingleResult(prev => ({
        ...prev,
        pullResult: response.data.data
      }));
      displayNotification(response.data.data.message || "Pull completed",
        response.data.data.success ? "success" : "warning");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to pull order");
      displayNotification("Pull failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // AUTO-SYNC - Single Order
  const autoSyncOrder = async () => {
    if (!bookingId.trim()) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${getBaseUrl()}/auto-sync/${bookingId.trim()}?priority=${syncPriority}`
      );
      setSingleResult(prev => ({
        ...prev,
        autoSyncResult: response.data.data
      }));
      displayNotification(response.data.data.message || "Auto-sync completed", "success");
      // Refresh check after auto-sync
      setTimeout(() => checkSingleOrder(), 1000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to auto-sync order");
      displayNotification("Auto-sync failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // PULL - Bulk Orders
  const pullBulkOrders = async () => {
    const ids = bulkBookingIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    if (ids.length === 0) {
      displayNotification("Please enter at least one Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${getBaseUrl()}/pull/bulk`, ids);
      setBulkResults(prev => ({
        ...prev,
        pullResults: response.data.data
      }));
      displayNotification(`Pulled ${ids.length} orders from Shypv`, "success");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to pull orders");
      displayNotification("Bulk pull failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // DELETE - Single Order from Local
  const deleteFromLocal = async (bkId = null) => {
    const idToDelete = bkId || bookingId.trim();
    if (!idToDelete) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${idToDelete} from LOCAL system?`)) {
      return;
    }

    setLoading(true);
    setError("");
    setDeleteResult(null);

    try {
      const response = await axios.delete(`${getBaseUrl()}/delete/local/${idToDelete}`);
      setDeleteResult(response.data.data);
      displayNotification(response.data.data.message || "Deleted from local", 
        response.data.data.success ? "success" : "warning");
      // Refresh check after delete
      if (!bkId) {
        setTimeout(() => checkSingleOrder(), 1000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete order");
      displayNotification("Delete failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // DELETE - Single Order from Shypv
  const deleteFromShypv = async (bkId = null) => {
    const idToDelete = bkId || bookingId.trim();
    if (!idToDelete) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${idToDelete} from SHYPV platform?`)) {
      return;
    }

    setLoading(true);
    setError("");
    setDeleteResult(null);

    try {
      const response = await axios.delete(`${getBaseUrl()}/delete/shypv/${idToDelete}`);
      setDeleteResult(response.data.data);
      displayNotification(response.data.data.message || "Deleted from Shypv", 
        response.data.data.success ? "success" : "warning");
      if (!bkId) {
        setTimeout(() => checkSingleOrder(), 1000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete from Shypv");
      displayNotification("Delete failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // DELETE - Single Order from Both Systems
  const deleteFromBoth = async (bkId = null) => {
    const idToDelete = bkId || bookingId.trim();
    if (!idToDelete) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${idToDelete} from BOTH systems? This cannot be undone!`)) {
      return;
    }

    setLoading(true);
    setError("");
    setDeleteResult(null);

    try {
      const response = await axios.delete(`${getBaseUrl()}/delete/both/${idToDelete}`);
      setDeleteResult(response.data.data);
      displayNotification(response.data.data.message || "Deleted from both systems", 
        response.data.data.success ? "success" : "warning");
      if (!bkId) {
        setTimeout(() => checkSingleOrder(), 1000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete from both systems");
      displayNotification("Delete failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // DELETE - Bulk Orders
  const deleteBulkOrders = async () => {
    const ids = bulkBookingIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    if (ids.length === 0) {
      displayNotification("Please enter at least one Booking ID", "error");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${ids.length} orders from ${deleteFrom}? This cannot be undone!`)) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.delete(`${getBaseUrl()}/delete/bulk`, {
        data: {
          bookingIds: ids,
          deleteFrom: deleteFrom
        }
      });
      setBulkResults(prev => ({
        ...prev,
        deleteResult: response.data.data
      }));
      const data = response.data.data;
      displayNotification(`Deleted: ${data.totalDeleted} success, ${data.totalFailed} failed`, 
        data.totalFailed > 0 ? "warning" : "success");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete orders");
      displayNotification("Bulk delete failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // VALIDATE - Single Order (Deep Analysis)
  const validateSingleOrder = async (bkId = null) => {
    const idToValidate = bkId || bookingId.trim();
    if (!idToValidate) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");
    setValidationResult(null);

    try {
      const response = await axios.get(`${getBaseUrl()}/validate/${idToValidate}`);
      setValidationResult(response.data.data);
      setSingleResult(prev => ({
        ...prev,
        validationResult: response.data.data
      }));
      displayNotification(response.data.data.message || "Validation completed", 
        response.data.data.status === "VALID" ? "success" : 
        response.data.data.status === "VALID_WITH_WARNINGS" ? "warning" : "error");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to validate order");
      displayNotification("Validation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // VALIDATE - Bulk Orders
  const validateBulkOrders = async () => {
    const ids = bulkBookingIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    if (ids.length === 0) {
      displayNotification("Please enter at least one Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${getBaseUrl()}/validate/bulk`, ids);
      setBulkResults(prev => ({
        ...prev,
        validationResults: response.data.data
      }));
      displayNotification(`Validated ${ids.length} orders`, "success");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to validate orders");
      displayNotification("Bulk validation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // ==================== CREATE/FULL SYNC FUNCTIONS ====================

  // CREATE - Create local order in Shypv
  const createInShypv = async (bkId = null, partnerIdVal = null) => {
    const idToCreate = bkId || bookingId.trim();
    if (!idToCreate) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (partnerIdVal) params.append('partnerId', partnerIdVal);
      
      const response = await axios.post(
        `${getBaseUrl()}/create/shypv/${idToCreate}${params.toString() ? '?' + params.toString() : ''}`
      );
      displayNotification(response.data.data.message || "Created in Shypv", 
        response.data.data.success ? "success" : "warning");
      if (!bkId) {
        setTimeout(() => checkSingleOrder(), 1000);
      }
      return response.data.data;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create in Shypv");
      displayNotification("Create in Shypv failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // CREATE - Create Shypv order locally
  const createLocally = async (bkId = null, partnerIdVal = null) => {
    const idToCreate = bkId || bookingId.trim();
    if (!idToCreate) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (partnerIdVal) params.append('partnerId', partnerIdVal);
      
      const response = await axios.post(
        `${getBaseUrl()}/create/local/${idToCreate}${params.toString() ? '?' + params.toString() : ''}`
      );
      displayNotification(response.data.data.message || "Created locally", 
        response.data.data.success ? "success" : "warning");
      if (!bkId) {
        setTimeout(() => checkSingleOrder(), 1000);
      }
      return response.data.data;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create locally");
      displayNotification("Create locally failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // FULL SYNC - Bi-directional sync ensuring order exists in both systems
  const fullSync = async (bkId = null, partnerIdVal = null, priority = 'local') => {
    const idToSync = bkId || bookingId.trim();
    if (!idToSync) {
      displayNotification("Please enter a Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (partnerIdVal) params.append('partnerId', partnerIdVal);
      params.append('priority', priority);
      
      const response = await axios.post(
        `${getBaseUrl()}/full/${idToSync}?${params.toString()}`
      );
      const result = response.data.data;
      displayNotification(result.message || `Full sync: ${result.status}`, 
        result.success ? "success" : "warning");
      if (!bkId) {
        setTimeout(() => checkSingleOrder(), 1000);
      }
      return result;
    } catch (err) {
      setError(err.response?.data?.message || "Full sync failed");
      displayNotification("Full sync failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // FULL SYNC - Bulk bi-directional sync
  const fullSyncBulk = async (priority = 'local') => {
    const ids = bulkBookingIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    if (ids.length === 0) {
      displayNotification("Please enter at least one Booking ID", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append('priority', priority);
      
      const response = await axios.post(
        `${getBaseUrl()}/full/bulk?${params.toString()}`,
        ids
      );
      setBulkResults(prev => ({
        ...prev,
        fullSyncResults: response.data.data
      }));
      const data = response.data.data;
      displayNotification(
        `Full Sync: ${data.success}/${data.total} success, ${data.createdInShypv} created in Shypv, ${data.createdLocally} created locally`, 
        data.failed > 0 ? "warning" : "success"
      );
    } catch (err) {
      setError(err.response?.data?.message || "Bulk full sync failed");
      displayNotification("Bulk full sync failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get sync status badge
  const getSyncStatusBadge = (status) => {
    const badgeStyles = {
      SYNCED: { style: styles.badgeSynced, icon: "✓", text: "Synced" },
      LOCAL_ONLY: { style: styles.badgeLocalOnly, icon: "⚠", text: "Local Only" },
      SHYPV_ONLY: { style: styles.badgeShypvOnly, icon: "ℹ", text: "Shypv Only" },
      SYNC_MISMATCH: { style: styles.badgeMismatch, icon: "⚡", text: "Mismatch" },
      NOT_FOUND: { style: styles.badgeNotFound, icon: "✗", text: "Not Found" },
      SUCCESS: { style: styles.badgeSuccess, icon: "✓", text: "Success" },
      FAILED: { style: styles.badgeFailed, icon: "✗", text: "Failed" },
      ALREADY_SYNCED: { style: styles.badgeSynced, icon: "✓", text: "Already Synced" },
      NOT_FOUND_LOCALLY: { style: styles.badgeLocalOnly, icon: "⚠", text: "Not Found Locally" },
      NOT_FOUND_IN_SHYPV: { style: styles.badgeShypvOnly, icon: "ℹ", text: "Not in Shypv" },
    };

    const badge = badgeStyles[status] || { style: {}, icon: "?", text: status };
    return (
      <span style={{ ...styles.badge, ...badge.style }}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  // Render Single Order Result
  const renderSingleResult = () => {
    if (!singleResult) return null;

    return (
      <div style={styles.resultCard}>
        <div style={styles.resultHeader}>
          <div>
            <span style={{ fontWeight: "600", fontSize: "16px", marginRight: "12px" }}>
              {singleResult.bookingId}
            </span>
            {getSyncStatusBadge(singleResult.syncStatus)}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {singleResult.foundInLocal && (
              <span style={{ ...styles.badge, backgroundColor: "#dbeafe", color: "#1d4ed8" }}>
                📦 Local
              </span>
            )}
            {singleResult.foundInShypv && (
              <span style={{ ...styles.badge, backgroundColor: "#d1fae5", color: "#065f46" }}>
                ☁️ Shypv
              </span>
            )}
          </div>
        </div>
        <div style={styles.resultBody}>
          <p style={{ color: "#6b7280", marginBottom: "20px", fontSize: "14px" }}>
            {singleResult.message}
          </p>

          {/* Duplicate verdict: the check now returns every local leg/copy, so a shipment that
              exists twice is called out here instead of the tool silently using one of them. */}
          {singleResult.duplicateWarning && (
            <div style={{ ...styles.alert, ...styles.alertError, marginBottom: "20px" }}>
              <i className="fa fa-clone" />
              <div style={{ flex: 1 }}>
                {singleResult.duplicateWarning}
                {singleResult.localRowCount > 1 && (
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>
                    {singleResult.localRowCount} local rows matched this AWB:{" "}
                    {(singleResult.localMatches || [])
                      .map((m) => `#${m.id} ${m.jobId} (${m.type}, ${m.status})`)
                      .join(" | ")}
                  </div>
                )}
              </div>
              <button
                style={{ ...styles.btnPrimary, whiteSpace: "nowrap" }}
                onClick={() => {
                  setDuplicateAwb(singleResult.bookingId || bookingId);
                  setMainView("duplicates");
                }}
              >
                <i className="fa fa-wrench" />
                Fix duplicates
              </button>
            </div>
          )}

          <div style={styles.dataGrid}>
            {/* Local Data */}
            <div style={styles.dataBox}>
              <div style={styles.dataBoxTitle}>
                <span style={{ fontSize: "18px" }}>📦</span>
                Local System
                {singleResult.foundInLocal ? (
                  <span style={{ color: "#10b981", fontSize: "12px" }}>● Found</span>
                ) : (
                  <span style={{ color: "#ef4444", fontSize: "12px" }}>● Not Found</span>
                )}
              </div>
              {singleResult.localData ? (
                <>
                  <div style={styles.dataItem}>
                    <span style={styles.dataLabel}>ID</span>
                    <span style={styles.dataValue}>{singleResult.localData.id}</span>
                  </div>
                  <div style={styles.dataItem}>
                    <span style={styles.dataLabel}>Job ID</span>
                    <span style={styles.dataValue}>{singleResult.localData.jobId}</span>
                  </div>
                  <div style={styles.dataItem}>
                    <span style={styles.dataLabel}>Status</span>
                    <span style={styles.dataValue}>{singleResult.localData.status}</span>
                  </div>
                  <div style={styles.dataItem}>
                    <span style={styles.dataLabel}>Type</span>
                    <span style={styles.dataValue}>{singleResult.localData.type}</span>
                  </div>
                  {singleResult.localData.customerName && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>Customer</span>
                      <span style={styles.dataValue}>{singleResult.localData.customerName}</span>
                    </div>
                  )}
                  {singleResult.localData.driverName && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>Driver</span>
                      <span style={styles.dataValue}>{singleResult.localData.driverName}</span>
                    </div>
                  )}
                  {singleResult.localData.codAmount !== undefined && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>COD Amount</span>
                      <span style={styles.dataValue}>{singleResult.localData.codAmount}</span>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ color: "#9ca3af", fontSize: "13px", textAlign: "center", padding: "20px 0" }}>
                  No data available
                </p>
              )}
            </div>

            {/* Shypv Data */}
            <div style={styles.dataBox}>
              <div style={styles.dataBoxTitle}>
                <span style={{ fontSize: "18px" }}>☁️</span>
                Shypv Platform
                {singleResult.foundInShypv ? (
                  <span style={{ color: "#10b981", fontSize: "12px" }}>● Found</span>
                ) : (
                  <span style={{ color: "#ef4444", fontSize: "12px" }}>● Not Found</span>
                )}
              </div>
              {singleResult.shypvData ? (
                <>
                  <div style={styles.dataItem}>
                    <span style={styles.dataLabel}>BKID</span>
                    <span style={styles.dataValue}>{singleResult.shypvData.bkid}</span>
                  </div>
                  {singleResult.shypvData.bkref && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>Reference</span>
                      <span style={styles.dataValue}>{singleResult.shypvData.bkref}</span>
                    </div>
                  )}
                  <div style={styles.dataItem}>
                    <span style={styles.dataLabel}>Status</span>
                    <span style={styles.dataValue}>{singleResult.shypvData.status}</span>
                  </div>
                  {singleResult.shypvData.statusDescription && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>Description</span>
                      <span style={styles.dataValue}>{singleResult.shypvData.statusDescription}</span>
                    </div>
                  )}
                  {singleResult.shypvData.customerName && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>Customer</span>
                      <span style={styles.dataValue}>{singleResult.shypvData.customerName}</span>
                    </div>
                  )}
                  {singleResult.shypvData.partnerName && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>Partner</span>
                      <span style={styles.dataValue}>{singleResult.shypvData.partnerName}</span>
                    </div>
                  )}
                  {singleResult.shypvData.codAmount !== undefined && (
                    <div style={styles.dataItem}>
                      <span style={styles.dataLabel}>COD Amount</span>
                      <span style={styles.dataValue}>{singleResult.shypvData.codAmount}</span>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ color: "#9ca3af", fontSize: "13px", textAlign: "center", padding: "20px 0" }}>
                  No data available
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons based on sync status */}
          <div style={styles.actionButtons}>
            {singleResult.syncStatus === "SYNC_MISMATCH" && (
              <>
                <button
                  style={styles.btnWarning}
                  onClick={() => pushSingleOrder(singleResult.bookingId)}
                  disabled={loading}
                >
                  <i className="fa fa-upload" /> Push Local → Shypv
                </button>
                <button
                  style={styles.btnPrimary}
                  onClick={() => pullSingleOrder(singleResult.bookingId)}
                  disabled={loading}
                >
                  <i className="fa fa-download" /> View Shypv Details
                </button>
                <button
                  style={{ ...styles.btnPrimary, backgroundColor: "#8b5cf6" }}
                  onClick={() => fullSync(singleResult.bookingId, null, 'local')}
                  disabled={loading}
                >
                  <i className="fa fa-sync" /> Full Sync
                </button>
              </>
            )}
            {singleResult.syncStatus === "LOCAL_ONLY" && (
              <>
                <div style={{ ...styles.alert, ...styles.alertWarning, marginTop: 0, marginBottom: 0, flex: 1 }}>
                  <i className="fa fa-exclamation-triangle" />
                  <span>
                    Order exists only locally. Click "Create in Shypv" to create it in the Shypv platform.
                  </span>
                </div>
                <button
                  style={{ ...styles.btnPrimary, backgroundColor: "#10b981", minWidth: "150px" }}
                  onClick={() => createInShypv(singleResult.bookingId)}
                  disabled={loading}
                >
                  <i className="fa fa-cloud-upload" /> Create in Shypv
                </button>
                <button
                  style={{ ...styles.btnPrimary, backgroundColor: "#8b5cf6", minWidth: "120px" }}
                  onClick={() => fullSync(singleResult.bookingId)}
                  disabled={loading}
                >
                  <i className="fa fa-sync" /> Full Sync
                </button>
              </>
            )}
            {singleResult.syncStatus === "SHYPV_ONLY" && (
              <>
                <div style={{ ...styles.alert, ...styles.alertInfo, marginTop: 0, marginBottom: 0, flex: 1 }}>
                  <i className="fa fa-info-circle" />
                  <span>
                    Order found only in Shypv. Click "Create Locally" to add it to TMS/Driver App.
                  </span>
                </div>
                <button
                  style={{ ...styles.btnPrimary, backgroundColor: "#3b82f6", minWidth: "150px" }}
                  onClick={() => createLocally(singleResult.bookingId)}
                  disabled={loading}
                >
                  <i className="fa fa-database" /> Create Locally
                </button>
                <button
                  style={{ ...styles.btnPrimary, backgroundColor: "#8b5cf6", minWidth: "120px" }}
                  onClick={() => fullSync(singleResult.bookingId)}
                  disabled={loading}
                >
                  <i className="fa fa-sync" /> Full Sync
                </button>
              </>
            )}
            {singleResult.syncStatus === "SYNCED" && (
              <div style={{ ...styles.alert, ...styles.alertSuccess, marginTop: 0, marginBottom: 0 }}>
                <i className="fa fa-check-circle" />
                <span>Order is synchronized in both systems. No action required.</span>
              </div>
            )}
            {singleResult.syncStatus === "NOT_FOUND" && (
              <div style={{ ...styles.alert, ...styles.alertError, marginTop: 0, marginBottom: 0 }}>
                <i className="fa fa-times-circle" />
                <span>Booking not found in either system. Please verify the Booking ID.</span>
              </div>
            )}
          </div>

          {/* Validation Result Display */}
          {singleResult.validationResult && (
            <div style={{ marginTop: "20px", padding: "16px", backgroundColor: 
              singleResult.validationResult.status === "VALID" ? "#f0fdf4" : 
              singleResult.validationResult.status === "VALID_WITH_WARNINGS" ? "#fefce8" : "#fef2f2",
              borderRadius: "8px", border: `1px solid ${
                singleResult.validationResult.status === "VALID" ? "#86efac" : 
                singleResult.validationResult.status === "VALID_WITH_WARNINGS" ? "#fde047" : "#fca5a5"
              }`
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <span style={{ fontSize: "18px" }}>
                  {singleResult.validationResult.status === "VALID" ? "✅" : 
                   singleResult.validationResult.status === "VALID_WITH_WARNINGS" ? "⚠️" : "❌"}
                </span>
                <span style={{ fontWeight: "600", fontSize: "15px" }}>
                  Deep Validation: {singleResult.validationResult.status}
                </span>
              </div>
              <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "12px" }}>
                {singleResult.validationResult.message}
              </p>
              
              {/* Field Comparisons */}
              {singleResult.validationResult.crossSystemValidation?.fieldComparisons && (
                <div style={{ marginTop: "12px" }}>
                  <p style={{ fontSize: "13px", fontWeight: "500", marginBottom: "8px" }}>Field Comparisons:</p>
                  <div style={{ display: "grid", gap: "8px" }}>
                    {singleResult.validationResult.crossSystemValidation.fieldComparisons.map((comp, idx) => (
                      <div key={idx} style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        padding: "8px 12px",
                        backgroundColor: comp.match ? "#f0fdf4" : "#fef2f2",
                        borderRadius: "6px",
                        fontSize: "13px"
                      }}>
                        <span style={{ fontWeight: "500", minWidth: "120px" }}>{comp.field}</span>
                        <span style={{ color: "#6b7280", flex: 1 }}>Local: {comp.localValue || 'N/A'}</span>
                        <span style={{ color: "#6b7280", flex: 1 }}>Shypv: {comp.shypvValue || 'N/A'}</span>
                        <span>{comp.match ? "✓" : "✗"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Issues */}
              {singleResult.validationResult.crossSystemValidation?.issues?.length > 0 && (
                <div style={{ marginTop: "12px" }}>
                  <p style={{ fontSize: "13px", fontWeight: "500", marginBottom: "8px" }}>Issues:</p>
                  {singleResult.validationResult.crossSystemValidation.issues.map((issue, idx) => (
                    <div key={idx} style={{ 
                      padding: "8px 12px", 
                      marginBottom: "6px",
                      backgroundColor: issue.severity === "ERROR" ? "#fee2e2" : issue.severity === "WARNING" ? "#fef3c7" : "#dbeafe",
                      borderRadius: "6px",
                      fontSize: "13px"
                    }}>
                      <span style={{ fontWeight: "500", color: issue.severity === "ERROR" ? "#dc2626" : issue.severity === "WARNING" ? "#d97706" : "#2563eb" }}>
                        [{issue.severity}]
                      </span> {issue.message}
                      {issue.recommendation && (
                        <p style={{ color: "#6b7280", marginTop: "4px", fontSize: "12px" }}>
                          💡 {issue.recommendation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Bulk Results
  const renderBulkResults = () => {
    if (!bulkResults) return null;

    const { summary, results } = bulkResults;

    return (
      <div>
        {/* Summary Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#374151" }}>{summary.total}</div>
            <div style={styles.statLabel}>Total Orders</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#10b981" }}>{summary.synced}</div>
            <div style={styles.statLabel}>Synced</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#f59e0b" }}>{summary.localOnly}</div>
            <div style={styles.statLabel}>Local Only</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#3b82f6" }}>{summary.shypvOnly}</div>
            <div style={styles.statLabel}>Shypv Only</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#ef4444" }}>{summary.notFound + (summary.mismatch || 0)}</div>
            <div style={styles.statLabel}>Issues</div>
          </div>
        </div>

        {/* Push Syncable Orders Button */}
        {(summary.synced > 0 || summary.mismatch > 0) && (
          <div style={{ marginBottom: "20px" }}>
            <button
              style={styles.btnWarning}
              onClick={() => {
                const pushableIds = results
                  .filter(r => r.syncStatus === "SYNCED" || r.syncStatus === "SYNC_MISMATCH")
                  .map(r => r.bookingId);
                pushBulkOrders(pushableIds);
              }}
              disabled={loading}
            >
              <i className="fa fa-upload" /> Push Syncable Orders ({summary.synced + (summary.mismatch || 0)})
            </button>
          </div>
        )}

        {/* Results Table */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Detailed Results</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Booking ID</th>
                  <th style={styles.th}>Sync Status</th>
                  <th style={styles.th}>Local Status</th>
                  <th style={styles.th}>Shypv Status</th>
                  <th style={styles.th}>Local</th>
                  <th style={styles.th}>Shypv</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={index}>
                    <td style={styles.td}>
                      <span style={{ fontWeight: "500" }}>{result.bookingId}</span>
                    </td>
                    <td style={styles.td}>{getSyncStatusBadge(result.syncStatus)}</td>
                    <td style={styles.td}>{result.localData?.status || "-"}</td>
                    <td style={styles.td}>{result.shypvData?.status || "-"}</td>
                    <td style={styles.td}>
                      {result.foundInLocal ? (
                        <span style={{ color: "#10b981" }}>✓</span>
                      ) : (
                        <span style={{ color: "#ef4444" }}>✗</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {result.foundInShypv ? (
                        <span style={{ color: "#10b981" }}>✓</span>
                      ) : (
                        <span style={{ color: "#ef4444" }}>✗</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Push Summary if available */}
        {bulkResults.pushSummary && (
          <div style={{ ...styles.alert, ...styles.alertInfo, marginTop: "20px" }}>
            <i className="fa fa-info-circle" />
            <span>
              Push completed: {bulkResults.pushSummary.success} success, {bulkResults.pushSummary.failed} failed
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* CSS Animation */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
            to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          input:focus, textarea:focus, select:focus {
            border-color: #4f7df3 !important;
            box-shadow: 0 0 0 3px rgba(79, 125, 243, 0.1) !important;
          }
        `}
      </style>

      {/* Notification */}
      {showNotification && (
        <div
          style={{
            ...styles.notification,
            ...(notificationType === "success"
              ? styles.notificationSuccess
              : notificationType === "error"
              ? styles.notificationError
              : styles.notificationWarning),
          }}
        >
          <i
            className={`fa ${
              notificationType === "success"
                ? "fa-check-circle"
                : notificationType === "error"
                ? "fa-times-circle"
                : "fa-exclamation-circle"
            }`}
            style={{ fontSize: "20px" }}
          />
          {notificationMessage}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <i className="fa fa-refresh" style={{ marginRight: "10px", color: "#4f7df3" }} />
          Order Sync
        </h1>
        <div style={{ display: "flex", gap: "10px" }}>
          <span style={{ ...styles.badge, backgroundColor: "#dbeafe", color: "#1d4ed8", padding: "8px 16px" }}>
            <i className="fa fa-server" style={{ marginRight: "6px" }} />
            Local + Shypv Sync
          </span>
        </div>
      </div>

      {/* Info Alert */}
      <div style={{ ...styles.alert, ...styles.alertInfo }}>
        <i className="fa fa-info-circle" />
        <div>
          <strong>About Order Sync:</strong> Check order status between Local System and Shypv Platform. 
          Use <strong>PUSH</strong> to send local status updates to Shypv, or <strong>PULL</strong> to view Shypv details.
          <br />
          <span style={{ fontSize: "12px", color: "#10b981" }}>
            ✅ NEW: Full Sync can now CREATE orders if missing in either system!
          </span>
        </div>
      </div>

      {/* View Toggle: Single vs Bulk */}
      <div style={styles.toggleContainer}>
        <button
          style={{
            ...styles.toggleBtn,
            ...(mainView === "single" ? styles.toggleBtnActive : styles.toggleBtnInactive),
          }}
          onClick={() => {
            setMainView("single");
            setBulkResults(null);
          }}
        >
          <i className="fa fa-search" />
          Single Order
        </button>
        <button
          style={{
            ...styles.toggleBtn,
            ...(mainView === "bulk" ? styles.toggleBtnActive : styles.toggleBtnInactive),
          }}
          onClick={() => {
            setMainView("bulk");
            setSingleResult(null);
          }}
        >
          <i className="fa fa-list" />
          Bulk Orders
        </button>
        <button
          style={{
            ...styles.toggleBtn,
            ...(mainView === "duplicates" ? styles.toggleBtnActive : styles.toggleBtnInactive),
          }}
          onClick={() => setMainView("duplicates")}
        >
          <i className="fa fa-clone" />
          Duplicates
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>
          <i className="fa fa-exclamation-circle" />
          {error}
          <button
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}
            onClick={() => setError("")}
          >
            <i className="fa fa-times" />
          </button>
        </div>
      )}

      {/* Duplicates View - find and repair one shipment written twice */}
      {mainView === "duplicates" && <OrderDuplicates initialAwb={duplicateAwb || bookingId} />}

      {/* Single Order View */}
      {mainView === "single" && (
        <>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fa fa-search" style={{ marginRight: "8px" }} />
                Check Single Order
              </h3>
            </div>
            <div style={styles.cardBody}>
              <div style={{ ...styles.formRow, alignItems: "flex-end" }}>
                <div style={{ ...styles.formGroup, flex: 2 }}>
                  <label style={styles.label}>Booking ID (AWB)</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={bookingId}
                    onChange={(e) => setBookingId(e.target.value)}
                    placeholder="Enter Booking ID (e.g., UB32-26C0454)"
                    onKeyPress={(e) => e.key === "Enter" && checkSingleOrder()}
                  />
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Sync Priority</label>
                  <select
                    style={styles.select}
                    value={syncPriority}
                    onChange={(e) => setSyncPriority(e.target.value)}
                  >
                    <option value="shypv">Shypv Priority</option>
                    <option value="local">Local Priority</option>
                  </select>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    style={styles.btnPrimary}
                    onClick={checkSingleOrder}
                    disabled={loading}
                  >
                    {loading ? <span style={styles.spinner} /> : <i className="fa fa-search" />}
                    Check
                  </button>
                  <button
                    style={styles.btnSuccess}
                    onClick={autoSyncOrder}
                    disabled={loading || !bookingId.trim()}
                  >
                    {loading ? <span style={styles.spinner} /> : <i className="fa fa-magic" />}
                    Auto-Sync
                  </button>
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div style={{ marginTop: "16px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <button
                  style={{ ...styles.btnSecondary, ...styles.btnSmall }}
                  onClick={() => pushSingleOrder()}
                  disabled={loading || !bookingId.trim()}
                >
                  <i className="fa fa-upload" /> Push Status
                </button>
                <button
                  style={{ ...styles.btnSecondary, ...styles.btnSmall }}
                  onClick={() => pullSingleOrder()}
                  disabled={loading || !bookingId.trim()}
                >
                  <i className="fa fa-download" /> Pull Details
                </button>
                <button
                  style={{ ...styles.btnSecondary, ...styles.btnSmall, backgroundColor: "#ede9fe", color: "#7c3aed", border: "1px solid #c4b5fd" }}
                  onClick={() => validateSingleOrder()}
                  disabled={loading || !bookingId.trim()}
                >
                  <i className="fa fa-check-double" /> Deep Validate
                </button>
              </div>

              {/* Delete Actions */}
              <div style={{ marginTop: "12px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>Delete:</span>
                <button
                  style={{ ...styles.btnDanger, ...styles.btnSmall, opacity: 0.85 }}
                  onClick={() => deleteFromLocal()}
                  disabled={loading || !bookingId.trim()}
                >
                  <i className="fa fa-trash" /> Local
                </button>
                <button
                  style={{ ...styles.btnDanger, ...styles.btnSmall, opacity: 0.85 }}
                  onClick={() => deleteFromShypv()}
                  disabled={loading || !bookingId.trim()}
                >
                  <i className="fa fa-trash" /> Shypv
                </button>
                <button
                  style={{ ...styles.btnDanger, ...styles.btnSmall }}
                  onClick={() => deleteFromBoth()}
                  disabled={loading || !bookingId.trim()}
                >
                  <i className="fa fa-trash" /> Both Systems
                </button>
              </div>
            </div>
          </div>

          {/* Single Order Result */}
          {renderSingleResult()}

          {/* Empty State */}
          {!singleResult && !loading && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🔍</div>
              <p style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
                Enter a Booking ID to check sync status
              </p>
              <p style={{ fontSize: "14px" }}>
                View order details from Local System and Shypv Platform side by side
              </p>
            </div>
          )}
        </>
      )}

      {/* Bulk Orders View */}
      {mainView === "bulk" && (
        <>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fa fa-list" style={{ marginRight: "8px" }} />
                Check Multiple Orders
              </h3>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Booking IDs (one per line or comma-separated)
                </label>
                <textarea
                  style={styles.textarea}
                  value={bulkBookingIds}
                  onChange={(e) => setBulkBookingIds(e.target.value)}
                  placeholder="UB32-26C0454&#10;UB32-26C0455&#10;UB32-26C0456"
                  rows={6}
                />
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "16px", flexWrap: "wrap" }}>
                <button
                  style={styles.btnPrimary}
                  onClick={checkBulkOrders}
                  disabled={loading}
                >
                  {loading ? <span style={styles.spinner} /> : <i className="fa fa-search" />}
                  Check All
                </button>
                <button
                  style={styles.btnSecondary}
                  onClick={pushBulkOrders}
                  disabled={loading || !bulkBookingIds.trim()}
                >
                  <i className="fa fa-upload" /> Push All
                </button>
                <button
                  style={styles.btnSecondary}
                  onClick={pullBulkOrders}
                  disabled={loading || !bulkBookingIds.trim()}
                >
                  <i className="fa fa-download" /> Pull All
                </button>
                <button
                  style={{ ...styles.btnPrimary, backgroundColor: "#8b5cf6" }}
                  onClick={() => fullSyncBulk('local')}
                  disabled={loading || !bulkBookingIds.trim()}
                >
                  <i className="fa fa-sync" /> Full Sync All
                </button>
                <button
                  style={{ ...styles.btnSecondary, backgroundColor: "#ede9fe", color: "#7c3aed", border: "1px solid #c4b5fd" }}
                  onClick={validateBulkOrders}
                  disabled={loading || !bulkBookingIds.trim()}
                >
                  <i className="fa fa-check-double" /> Validate All
                </button>
                <button
                  style={styles.btnSecondary}
                  onClick={() => {
                    setBulkBookingIds("");
                    setBulkResults(null);
                  }}
                >
                  <i className="fa fa-eraser" /> Clear
                </button>
              </div>

              {/* Bulk Delete Section */}
              <div style={{ marginTop: "12px", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>Bulk Delete:</span>
                <select
                  style={{ ...styles.select, width: "auto", minWidth: "140px" }}
                  value={deleteFrom}
                  onChange={(e) => setDeleteFrom(e.target.value)}
                >
                  <option value="LOCAL">Local Only</option>
                  <option value="SHYPV">Shypv Only</option>
                  <option value="BOTH">Both Systems</option>
                </select>
                <button
                  style={{ ...styles.btnDanger, ...styles.btnSmall }}
                  onClick={deleteBulkOrders}
                  disabled={loading || !bulkBookingIds.trim()}
                >
                  <i className="fa fa-trash" /> Delete Selected
                </button>
                <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: "13px" }}>
                  {bulkBookingIds.split(/[\n,]/).filter(id => id.trim()).length} orders entered
                </span>
              </div>
            </div>
          </div>

          {/* Bulk Results */}
          {renderBulkResults()}

          {/* Empty State */}
          {!bulkResults && !loading && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📋</div>
              <p style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
                Enter multiple Booking IDs to check in bulk
              </p>
              <p style={{ fontSize: "14px" }}>
                Quickly identify sync status for multiple orders at once
              </p>
            </div>
          )}
        </>
      )}

      {/* Status Legend */}
      <div style={{ ...styles.card, marginTop: "24px" }}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <i className="fa fa-info-circle" style={{ marginRight: "8px" }} />
            Sync Status Reference
          </h3>
        </div>
        <div style={styles.cardBody}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              {getSyncStatusBadge("SYNCED")}
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Order exists in both systems with matching status
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              {getSyncStatusBadge("LOCAL_ONLY")}
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Order exists only in local system
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              {getSyncStatusBadge("SHYPV_ONLY")}
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Order exists only in Shypv platform
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              {getSyncStatusBadge("SYNC_MISMATCH")}
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Order in both but status differs
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              {getSyncStatusBadge("NOT_FOUND")}
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Order not found in either system
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
